import { Injectable } from '@angular/core';
import {
  Branding,
  BrandingHeaderItem,
  BrandingLogoSettings,
  BrandingHeaderItemPut,
  BrandingLogoSettingsPut,
} from './../../model/branding.model';
import { UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { HeaderItem, MenuItem } from '@app/shell/header/model/header.model';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class BrandingConfiguratorService {
  branding: Branding;

  colorsForm: UntypedFormGroup;
  logoForm: UntypedFormGroup;
  mainMenuItems: BrandingHeaderItemPut[];
  dropDownMenuItems: BrandingHeaderItemPut[];
  /** enable live preview in header bar? */
  previewActive: Subject<boolean> = new Subject<boolean>();

  get primaryColor(): string {
    return this.colorsForm.get('colors').value['primary'];
  }

  get secondaryColor(): string {
    return this.colorsForm.get('colors').value['secondary'];
  }

  get logoFile() {
    if (this.logoForm) {
      return this.logoForm.get('companyLogoFileBase64').value;
    }
    return null;
  }

  get logoFileUrl() {
    if (this.logoForm) {
      return this.logoForm.get('logoUrl').value;
    }
    return null;
  }

  get logoFileName() {
    if (this.logoForm) {
      return this.logoForm.get('companyLogo').value;
    }
    return null;
  }

  get customColorsActive() {
    if (this.colorsForm) {
      return this.colorsForm.get('enabled').value;
    } else {
      return false;
    }
  }

  get customLogoActive() {
    if (this.logoForm) {
      return this.logoForm.get('companyStatus').value;
    }
    return false;
  }

  constructor(private domSanitizer: DomSanitizer) {}

  /**
   * converts logo data from get endpoint to put-friendly
   */
  logoToPut(logo: BrandingLogoSettings): BrandingLogoSettingsPut {
    return {
      companyStatus: logo.companyStatus,
      logoUrl: logo.logoUrl,
      logoUrlOpenInNewTab: logo.logoUrlOpenInNewTab,
      /** logo file name */
      companyLogo: logo.companyLogo,
      companyLogoPath: logo.companyLogoPath,
      companyLogoFileBase64: '',
    };
  }

  menuItemToPut(item: BrandingHeaderItem): BrandingHeaderItemPut {
    return {
      i18n: {
        name: {
          en: item.name,
          fr: item.name,
          de: item.name,
        },
      },
      url: item.url,
      iconName: item.iconName,
      iconFileBase64: '',
      iconUrl: item.image,
    };
  }

  menuPutToHeaderItem(item: BrandingHeaderItemPut): HeaderItem {
    return {
      id: 'id',
      name: item.i18n.name.en,
      internal: false,
      url: item.url,
      image: this.converImage(item.iconFileBase64, item.iconName),
      imageBase64: item.iconFileBase64,
      imageFileName: item.iconName,
    };
  }

  menuPutToMenuItem(item: BrandingHeaderItemPut): MenuItem {
    return {
      id: 'id',
      internal: false,
      name: item.i18n.name.en,
      url: item.url,
      image: this.converImage(item.iconFileBase64, item.iconName),
      imageBase64: item.iconFileBase64,
      imageFileName: item.iconName,
    };
  }

  updateColor(type: 'primary' | 'secondary', color: string) {
    let primary = this.primaryColor;
    let secondary = this.secondaryColor;

    switch (type) {
      case 'primary':
        primary = color;
        break;
      case 'secondary':
        secondary = color;
        break;
    }

    this.colorsForm.get('colors').patchValue({
      primary: primary,
      secondary: secondary,
    });
  }

  addNewMainMenuItem() {
    this.mainMenuItems.push(this.getDefaultMenuItemData());
  }

  addNewDropDownMenuItem() {
    this.dropDownMenuItems.push(this.getDefaultMenuItemData());
  }

  /** convert main menu items to Header Items for header preview */
  getMenuItemsAsHeaderItems(): HeaderItem[] {
    const result: HeaderItem[] = [];
    this.mainMenuItems.forEach((item) => {
      const putItem = this.menuPutToHeaderItem(item);
      result.push(putItem);
    });
    return result;
  }

  /** convert main menu items to Menu Items for dropdown preview */
  getMenuItemsAsMenuItems(): MenuItem[] {
    const result: MenuItem[] = [];
    this.dropDownMenuItems.forEach((item) => {
      const putItem = this.menuPutToMenuItem(item);
      result.push(putItem);
    });
    return result;
  }

  private getDefaultMenuItemIconBase64() {
    return 'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNCIgaGVpZ2h0PSIyNSIgdmlld0JveD0iMCAwIDE0IDI1Ij4KICAgIDxwYXRoIGZpbGw9IiMyRTM4NEEiIGZpbGwtcnVsZT0iZXZlbm9kZCIgZD0iTTIuNjY1IDIwLjQ2M2wzLjIxMi0zLjIxMmExLjI1OSAxLjI1OSAwIDAgMSAxLjc4LS4wMTJsMy4xNjkgMy4xNy4xMTMtMTcuMDYtOC4xNjEuMDU1LS4xMTMgMTcuMDU5em0xMC4yODMgMy44NTlhMS4yOSAxLjI5IDAgMCAxLS40MS4yNzYgMS4yNTIgMS4yNTIgMCAwIDEtMS4zNy0uMjY0bC00LjQyLTQuNDItNC40NzggNC40NzlhMS4yNjkgMS4yNjkgMCAwIDEtMS4zNzMuMjgyIDEuMjM4IDEuMjM4IDAgMCAxLS43NjktMS4xNThMLjI3IDIuMTYyQTEuMjc3IDEuMjc3IDAgMCAxIDEuNTM2Ljg5NmwxMC42NzgtLjA3YTEuMjM3IDEuMjM3IDAgMCAxIDEuMjUgMS4yNDlsLS4xNDIgMjEuMzU1Yy0uMDAyLjM0LS4xNDEuNjU5LS4zNzQuODkyeiIvPgo8L3N2Zz4K';
  }

  private getDefaultMenuItemData(): BrandingHeaderItemPut {
    return {
      i18n: {
        name: {
          en: 'New item',
          fr: 'New item',
          de: 'New item',
        },
      },
      url: `https://app.socialtalent.com`,
      iconName: 'bookmark.svg',
      iconFileBase64: this.getDefaultMenuItemIconBase64(),
      iconUrl: '',
    };
  }

  /**
   * Convert SVG Image
   */
  private converImage(itemBase64: string, itemFileName: string) {
    let extension = itemFileName.match(/\.[0-9a-z]+$/i)[0].replace('.', '');
    if (extension === 'svg') {
      extension += '+xml';
    }
    return this.domSanitizer.bypassSecurityTrustUrl(`data:image/${extension};base64,${itemBase64}`);
  }
}
